import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from "@angular/router";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AppPermissionService } from "@core/services/app-permission.service";
import { map, take } from "rxjs/operators";

@Injectable()
export class RoleAccessGuard implements CanActivate, CanActivateChild {
  constructor(private appPermissionService: AppPermissionService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkRoutePermissions(route, state);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkRoutePermissions(childRoute, state);
  }

  private checkRoutePermissions(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.appPermissionService.getAppPermissions().pipe(
      take(1), // Take only the first emission to avoid memory leaks
      map((appPermissions) => {
        const requiredPermissions = route.data["permissions"];

        if (!requiredPermissions) {
          // If no permission is required, allow access
          return true;
        }

        // Check if user has the required permission
        const hasPermission = this.checkPermission(appPermissions, requiredPermissions);

        if (!hasPermission) {
          // Navigate to access denied (403) page if permission is denied
          this.router.navigate(["/access-denied"]);
          return false;
        }

        return true;
      })
    );
  }

  private checkPermission(appPermissions: any, requiredPermissions: string | string[]): boolean {
    // Handle single permission
    if (typeof requiredPermissions === "string") {
      return !!appPermissions[requiredPermissions];
    }

    // Handle multiple permissions (OR logic - operator needs at least one)
    if (Array.isArray(requiredPermissions)) {
      return requiredPermissions.some((permission) => !!appPermissions[permission]);
    }

    return false;
  }
}
